import { useState, useEffect } from 'react';
import { 
  Search, CreditCard, CheckCircle, Clock, AlertCircle,
  DollarSign, Loader2, User
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getStatusConfig(status) {
  if (status === 'Vigente') return { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20' };
  if (status === 'Por Vencer') return { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' };
  if (status === 'Vencida') return { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' };
  return { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' };
}

export function AdminMemberships() {
  const { token } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState({ vigentes: 0, porVencer: 0, vencidas: 0, ingresos: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setIsLoading(true);
    try {
      const [membershipsRes, professionalsRes, statsRes] = await Promise.all([
        fetch(`${API}/admin/memberships`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/professionals`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/stats`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (membershipsRes.ok) setMemberships(await membershipsRes.json());
      if (professionalsRes.ok) setProfessionals(await professionalsRes.json());
      if (statsRes.ok) {
        const s = await statsRes.json();
        setStats({
          vigentes: s.active_memberships || 0,
          porVencer: s.expiring_memberships || 0,
          vencidas: s.expired_memberships || 0,
          ingresos: s.total_revenue || 0
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function getProfessionalForMembership(membership) {
    for (let i = 0; i < professionals.length; i++) {
      if (professionals[i].user_id === membership.user_id) {
        return professionals[i];
      }
    }
    return null;
  }

  const filteredMemberships = memberships.filter(function(m) {
    const prof = getProfessionalForMembership(m);
    const matchesSearch = !search || 
      (prof && prof.nombre && prof.nombre.toLowerCase().includes(search.toLowerCase())) ||
      (prof && prof.email && prof.email.toLowerCase().includes(search.toLowerCase()));
    const matchesStatus = !statusFilter || statusFilter === 'all' || m.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  function renderMembershipRow(membership) {
    const prof = getProfessionalForMembership(membership);
    const config = getStatusConfig(membership.status);

    return (
      <tr key={membership.id} className="border-b border-[#2a2a2a] hover:bg-[#1a1a1a]">
        <td className="py-4 px-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
              {prof && prof.foto ? <img src={prof.foto} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-gray-500" />}
            </div>
            <div>
              <p className="text-white font-medium">{prof ? prof.nombre : 'Sin asignar'}</p>
              <p className="text-sm text-gray-500">{prof ? prof.email : membership.user_id}</p>
            </div>
          </div>
        </td>
        <td className="py-4 px-4">
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
            {membership.status}
          </span>
        </td>
        <td className="py-4 px-4">
          <span className="text-white">${membership.monto ? membership.monto.toLocaleString('es-MX') : 0} MXN</span>
        </td>
        <td className="py-4 px-4">
          <span className="text-gray-400 text-sm">{membership.fecha_inicio ? new Date(membership.fecha_inicio).toLocaleDateString('es-MX') : '-'}</span>
        </td>
        <td className="py-4 px-4">
          <span className="text-gray-400 text-sm">{membership.fecha_vencimiento ? new Date(membership.fecha_vencimiento).toLocaleDateString('es-MX') : '-'}</span>
        </td>
      </tr>
    );
  }

  const rows = [];
  for (let i = 0; i < filteredMemberships.length; i++) {
    rows.push(renderMembershipRow(filteredMemberships[i]));
  }

  return (
    <div className="p-8" data-testid="admin-memberships">
      <div className="mb-8">
        <h1 className="font-manrope font-bold text-2xl text-white mb-2">Gestión de Membresías</h1>
        <p className="text-gray-400">Control de membresías y pagos del Colegio</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-4">
          <div className="flex items-center gap-2 text-green-400 mb-2"><CheckCircle className="w-5 h-5" /><span className="text-sm">Vigentes</span></div>
          <p className="text-2xl font-bold text-white">{stats.vigentes}</p>
        </div>
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-4">
          <div className="flex items-center gap-2 text-amber-400 mb-2"><AlertCircle className="w-5 h-5" /><span className="text-sm">Por Vencer</span></div>
          <p className="text-2xl font-bold text-white">{stats.porVencer}</p>
        </div>
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-400 mb-2"><Clock className="w-5 h-5" /><span className="text-sm">Vencidas</span></div>
          <p className="text-2xl font-bold text-white">{stats.vencidas}</p>
        </div>
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-4">
          <div className="flex items-center gap-2 text-white mb-2"><DollarSign className="w-5 h-5" /><span className="text-sm">Ingresos</span></div>
          <p className="text-2xl font-bold text-white">${stats.ingresos.toLocaleString('es-MX')}</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input placeholder="Buscar por nombre o email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-[#111111] border-[#2a2a2a] text-white" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
          <SelectTrigger className="w-full md:w-48 bg-[#111111] border-[#2a2a2a] text-white">
            <SelectValue placeholder="Filtrar por estatus" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="Vigente">Vigente</SelectItem>
            <SelectItem value="Por Vencer">Por Vencer</SelectItem>
            <SelectItem value="Vencida">Vencida</SelectItem>
            <SelectItem value="Pendiente de Pago">Pendiente de Pago</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2a2a2a] bg-[#0a0a0a]">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Profesional</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Estatus</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Monto</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Inicio</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Vencimiento</th>
                </tr>
              </thead>
              <tbody>{rows.length > 0 ? rows : <tr><td colSpan={5} className="py-12 text-center text-gray-500">No se encontraron membresías</td></tr>}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
